import { useEffect, useMemo, useState, type CSSProperties } from "react";
import { useLocation, useNavigate } from "react-router";
import { where } from "firebase/firestore";
import { ChevronLeft, ChevronRight, Plus } from "lucide-react";
import {
  STATUS_LABEL,
  addDays,
  clinicMinutesOfDay,
  clinicTimeToUtc,
  clinicWeekday,
  formatClinicDate,
  formatClinicTime,
  minutesToHHMM,
  toClinicDate,
  type Appointment,
  type AppointmentStatus,
  type AvailabilityRule,
} from "@cop/domain";
import { useCatalog, useLive } from "../lib/data";
import { useSession } from "../lib/session";
import { AppointmentPanel } from "../components/AppointmentPanel";
import { NewAppointmentPanel, type NewAppointmentPrefill } from "../components/NewAppointmentPanel";

const DAY_START = 8 * 60;
const DAY_END = 20 * 60;
const STEP = 15;
const ROWS = (DAY_END - DAY_START) / STEP;

type View = "day" | "week";

function weekStart(date: string) {
  return addDays(date, 1 - clinicWeekday(date));
}

function useNow() {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 60_000);
    return () => clearInterval(t);
  }, []);
  return now;
}

export function Agenda() {
  const nav = useNavigate();
  const location = useLocation();
  const { practitionerId: myPractitioner, role } = useSession();
  const { practitioners, services, ready } = useCatalog();
  const today = toClinicDate(new Date());
  const [date, setDate] = useState(today);
  const [view, setView] = useState<View>("day");
  const [selected, setSelected] = useState<string | null>(null);
  const [creating, setCreating] = useState<NewAppointmentPrefill | null>((location.state as { prefill?: NewAppointmentPrefill } | null)?.prefill ?? null);
  const [hidden, setHidden] = useState<Set<string>>(new Set());
  const now = useNow();

  useEffect(() => {
    if (role === "dentist" || role === "specialist") {
      if (myPractitioner) setHidden(new Set(practitioners.filter((p) => p.id !== myPractitioner).map((p) => p.id)));
    }
  }, [role, myPractitioner, practitioners]);

  useEffect(() => {
    if (location.state) nav(location.pathname, { replace: true, state: null });
  }, [location, nav]);

  const days = view === "day" ? [date] : Array.from({ length: 6 }, (_, i) => addDays(weekStart(date), i));
  const rangeStart = clinicTimeToUtc(days[0]!, 0).toISOString();
  const rangeEnd = clinicTimeToUtc(addDays(days.at(-1)!, 1), 0).toISOString();
  const { data: appts } = useLive<Appointment>("appointments", [where("start", ">=", rangeStart), where("start", "<", rangeEnd)], [rangeStart, rangeEnd]);
  const { data: rules } = useLive<AvailabilityRule>("availabilityRules", []);

  const visible = practitioners.filter((p) => !hidden.has(p.id));
  const list = (appts ?? []).filter((a) => !hidden.has(a.practitionerId) && a.status !== "cancelled");
  const selectedAppt = (appts ?? []).find((a) => a.id === selected) ?? null;

  const counts = useMemo(() => {
    const todays = (appts ?? []).filter((a) => toClinicDate(new Date(a.start)) === date && a.status !== "cancelled");
    const by = (s: AppointmentStatus[]) => todays.filter((a) => s.includes(a.status)).length;
    return {
      total: todays.length,
      pending: by(["requested", "pending_confirmation"]),
      inClinic: by(["arrived", "waiting", "in_treatment"]),
      done: by(["completed"]),
      noShow: by(["no_show"]),
    };
  }, [appts, date]);

  const columns =
    view === "day"
      ? visible.map((p) => ({ key: p.id, label: p.displayName, sub: p.title, color: p.color, date, practitionerId: p.id }))
      : days.map((d) => ({ key: d, label: formatClinicDate(`${d}T15:00:00Z`, { weekday: "short", day: "numeric", month: undefined }), sub: "", color: "", date: d, practitionerId: null as string | null }));

  const workingBands = (col: (typeof columns)[number]) => {
    const wd = clinicWeekday(col.date);
    return (rules ?? []).filter((r) => r.weekday === wd && (col.practitionerId ? r.practitionerId === col.practitionerId : !hidden.has(r.practitionerId)));
  };

  const shift = (n: number) => setDate((d) => addDays(d, view === "day" ? n : n * 7));
  const nowMinutes = clinicMinutesOfDay(now);

  return (
    <div className="page agenda" data-panel={selectedAppt || creating ? "open" : undefined}>
      <header className="toolbar">
        <div className="toolbar-title">
          <h1 className="cap">{view === "day" ? formatClinicDate(`${date}T15:00:00Z`) : `Semana del ${formatClinicDate(`${days[0]}T15:00:00Z`, { weekday: undefined })}`}</h1>
          {view === "day" && (
            <p className="muted small">
              {counts.total} turnos · {counts.pending} por confirmar · {counts.inClinic} en clínica · {counts.done} finalizados
              {counts.noShow > 0 && ` · ${counts.noShow} ausentes`}
            </p>
          )}
        </div>
        <div className="row gap-8 wrap">
          <div className="row gap-4">
            <button className="btn btn-sm" onClick={() => shift(-1)} aria-label="Anterior">
              <ChevronLeft size={16} aria-hidden />
            </button>
            <button className="btn btn-sm" onClick={() => setDate(today)} disabled={date === today}>
              Hoy
            </button>
            <button className="btn btn-sm" onClick={() => shift(1)} aria-label="Siguiente">
              <ChevronRight size={16} aria-hidden />
            </button>
            <input type="date" className="date-input" value={date} onChange={(e) => e.target.value && setDate(e.target.value)} aria-label="Ir a fecha" />
          </div>
          <div className="segmented" role="tablist" aria-label="Vista">
            <button role="tab" aria-selected={view === "day"} onClick={() => setView("day")}>
              Día
            </button>
            <button role="tab" aria-selected={view === "week"} onClick={() => setView("week")}>
              Semana
            </button>
          </div>
          <button className="btn btn-primary btn-sm" onClick={() => (setSelected(null), setCreating({}))}>
            <Plus size={16} aria-hidden /> Nuevo turno
          </button>
        </div>
      </header>

      <div className="filters" role="group" aria-label="Profesionales">
        {practitioners.map((p) => (
          <label key={p.id} className="filter-chip">
            <input
              type="checkbox"
              checked={!hidden.has(p.id)}
              onChange={() =>
                setHidden((h) => {
                  const n = new Set(h);
                  n.has(p.id) ? n.delete(p.id) : n.add(p.id);
                  return n;
                })
              }
            />
            <span className="dot" style={{ background: p.color }} aria-hidden />
            {p.displayName}
          </label>
        ))}
        <span className="legend">
          {(["pending_confirmation", "confirmed", "arrived", "in_treatment", "completed"] as AppointmentStatus[]).map((s) => (
            <span key={s} className="legend-item" data-status={s}>
              <span className="legend-swatch" aria-hidden /> {STATUS_LABEL[s]}
            </span>
          ))}
        </span>
      </div>

      <div className="agenda-body">
        <div className="calendar" style={{ "--cols": Math.max(columns.length, 1), "--rows": ROWS } as CSSProperties}>
          <div className="cal-head">
            <div className="cal-corner" />
            {columns.map((c) => (
              <div key={c.key} className="cal-col-head" data-today={view === "week" && c.date === today ? true : undefined}>
                {c.color && <span className="dot" style={{ background: c.color }} aria-hidden />}
                <div>
                  <strong className="cap">{c.label}</strong>
                  {c.sub && <span className="muted small">{c.sub}</span>}
                </div>
              </div>
            ))}
          </div>
          <div className="cal-grid">
            <div className="cal-times" aria-hidden>
              {Array.from({ length: ROWS / 4 }, (_, i) => (
                <span key={i} style={{ gridRow: `${i * 4 + 1} / span 4` }} className="mono">
                  {minutesToHHMM(DAY_START + i * 60)}
                </span>
              ))}
            </div>
            {!ready && <div className="cal-loading">Cargando agenda…</div>}
            {columns.map((c) => {
              const colAppts = list.filter((a) => toClinicDate(new Date(a.start)) === c.date && (!c.practitionerId || a.practitionerId === c.practitionerId));
              const lanes = view === "week" ? visible.map((p) => p.id) : [c.practitionerId!];
              return (
                <div key={c.key} className="cal-col" aria-label={`${c.label}: ${colAppts.length} turnos`} role="list">
                  {workingBands(c).map((r) => (
                    <div
                      key={r.id}
                      className="cal-band"
                      style={{
                        top: `calc(${(r.startMinutes - DAY_START) / STEP} * var(--row))`,
                        height: `calc(${(r.endMinutes - r.startMinutes) / STEP} * var(--row))`,
                        left: view === "week" ? `${(lanes.indexOf(r.practitionerId) / lanes.length) * 100}%` : 0,
                        width: view === "week" ? `${100 / lanes.length}%` : "100%",
                      }}
                    />
                  ))}
                  {c.date === today && nowMinutes > DAY_START && nowMinutes < DAY_END && (
                    <div className="cal-now" style={{ top: `calc(${(nowMinutes - DAY_START) / STEP} * var(--row))` }} aria-hidden />
                  )}
                  {colAppts.map((a) => {
                    const s = clinicMinutesOfDay(new Date(a.start));
                    const e = clinicMinutesOfDay(new Date(a.end));
                    const lane = view === "week" ? Math.max(0, lanes.indexOf(a.practitionerId)) : 0;
                    const prac = practitioners.find((p) => p.id === a.practitionerId);
                    return (
                      <button
                        key={a.id}
                        role="listitem"
                        className="appt"
                        data-status={a.status}
                        data-selected={selected === a.id || undefined}
                        data-compact={e - s <= 30 || view === "week" || undefined}
                        style={{
                          top: `calc(${(s - DAY_START) / STEP} * var(--row) + 1px)`,
                          height: `calc(${(e - s) / STEP} * var(--row) - 2px)`,
                          left: `calc(${(lane / lanes.length) * 100}% + 2px)`,
                          width: `calc(${100 / lanes.length}% - 4px)`,
                        }}
                        onClick={() => (setCreating(null), setSelected(a.id))}
                        aria-label={`${formatClinicTime(a.start)} ${a.patientName}, ${a.serviceName}, ${STATUS_LABEL[a.status]}`}
                      >
                        <span className="appt-time mono">
                          {view === "week" && <span className="dot" style={{ background: prac?.color }} aria-hidden />}
                          {formatClinicTime(a.start)}
                        </span>
                        <span className="appt-name">{a.patientName}</span>
                        <span className="appt-service">{a.serviceName}</span>
                      </button>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>

        {selectedAppt && !creating && <AppointmentPanel appt={selectedAppt} practitioners={practitioners} onClose={() => setSelected(null)} />}
        {creating && (
          <NewAppointmentPanel
            date={date >= today ? date : today}
            services={services}
            practitioners={practitioners}
            prefill={creating}
            onClose={() => setCreating(null)}
            onCreated={(id) => {
              setCreating(null);
              setSelected(id);
            }}
          />
        )}
      </div>
    </div>
  );
}
